import React, { useEffect, useState, useMemo, Fragment, useCallback, MutableRefObject } from 'react';
import {
  ColumnContainerStyled,
  PlanningActionContainer,
  PlanningContentContainer,
  WeekPlanDetailScrollable,
  WeekPlanDetailContainer,
  WeekColumnDroppableStyled,
} from '../styled';
import { WorkPackageDetailHeader } from '@bechtel/client-work-packages';
import { PlanningActions } from './actions';
import { CommodityCards } from './commodity';
import { PlanningWeekCard } from './weekplancard';
import { Backlog } from './backlog';
import {
  Box,
  Spinner,
  SpinnerSize,
  VisuallyHidden,
  useToastsDispatch,
  ToastAction,
  ToastType,
  Text,
} from '@bechtel/gds';
import { NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import { DragDropContext, Droppable, DroppableProvided, DragStart, DroppableStateSnapshot } from 'react-beautiful-dnd';
import { GET_WORKPACKAGE_ADDITIONAL_INFO_BY_IDS, GET_PLANNED_MILESTONES, SAVE_PLAN, UNPLAN } from '@bechtel/client-gql';
import { get, map, sortBy, nth, find, filter, forEach, noop } from 'lodash';
import {
  WorkPackage,
  PersonaType,
  PlanningFilterInput,
  PlanningWeek,
  PlanningPayload,
  SavePlanningInput,
  CommodityMilestone,
  UnplanInput,
  PlanningWeekStats,
  PlanningError,
  CrewMemberExpiredMilestoneStats,
} from '@bechtel/types';
import {
  DND_IDENTIFIER,
  getSavePlanningOptimisticResponse,
  getUnplanOptimisticResponse,
  splitIdentifiers,
  updateCache,
  updateUnplanCache,
  deleteApproveClaimedMilestone,
  usePlanning,
  AllForemanId,
  UnassignedForemanId,
  TOAST_ID_SELECT_FOREMAN,
  ErrorRetry,
  useDebounce,
  useConfig,
} from '@bechtel/client-common';
import { MilestoneIdentifier } from '../types';
import { getPlanningWeekDataNoMilestones } from './commodity/card/form-data';
import { SavePlanError, PlanMilestoneError } from './error';
import { Trans, t } from '@lingui/macro';

interface PlannedMilestonesProps {
  setSelectedMilestoneDraggableIds: (value: string[] | []) => void;
  selectedMilestoneDraggableIds: string[];
  draggingMilestoneId: string;
  milestoneIdentifiers: MutableRefObject<Map<string, MilestoneIdentifier>>;
  plannedMilestonesData: MutableRefObject<PlanningWeek[]>;
}

const PlannedMilestones: React.FC<PlannedMilestonesProps> = ({
  setSelectedMilestoneDraggableIds,
  selectedMilestoneDraggableIds,
  draggingMilestoneId,
  milestoneIdentifiers,
  plannedMilestonesData,
}) => {
  const { selectedForeman } = usePlanning();
  const { numPlanningNextWeeks } = useConfig();
  const planningWeekDataNoMilestones = getPlanningWeekDataNoMilestones(numPlanningNextWeeks + 1);
  //Get Planned Milestones
  const [getPlannedMilestones, { loading, error, data, refetch, networkStatus }] = useLazyQuery<
    { plannedMilestones: PlanningWeek[] },
    {
      input: PlanningFilterInput;
    }
  >(GET_PLANNED_MILESTONES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  useEffect(() => {
    if (selectedForeman && !(selectedForeman.id === UnassignedForemanId || selectedForeman.id === AllForemanId)) {
      getPlannedMilestones({
        variables: {
          input: {
            persona: selectedForeman && selectedForeman.persona ? selectedForeman.persona : PersonaType.FOREMAN,
            crewMemberId: selectedForeman.id,
            numNextWeeks: numPlanningNextWeeks,
          },
        },
      });
    }
  }, [getPlannedMilestones, selectedForeman]);
  useEffect(() => {
    if (plannedMilestonesData) plannedMilestonesData.current = get(data, 'plannedMilestones', []);
  }, [plannedMilestonesData, data]);

  const plannedMilestones: PlanningWeek[] = useMemo(() => {
    return get(data, 'plannedMilestones', planningWeekDataNoMilestones);
  }, [data]);
  const plannedMilestonesSorted =
    selectedForeman?.id === UnassignedForemanId || selectedForeman?.id == AllForemanId
      ? planningWeekDataNoMilestones
      : sortBy(plannedMilestones, ['year', 'weekNumber']);

  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box data-testid="loading" css="width:100%;">
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
          >
            <Spinner size={SpinnerSize.MEDIUM} />
          </Box>
        </Box>
      )}
      {!loading && error && (
        <div data-testid="retry">
          <ErrorRetry
            refetch={() =>
              refetch({
                input: {
                  persona: selectedForeman && selectedForeman.persona ? selectedForeman.persona : PersonaType.FOREMAN,
                  crewMemberId: selectedForeman ? selectedForeman.id : '',
                  numNextWeeks: numPlanningNextWeeks,
                },
              })
            }
          >
            <Trans>There was a problem loading planned milestones. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </div>
      )}
      {!loading &&
        !error &&
        plannedMilestones.length > 0 &&
        map(plannedMilestonesSorted, (plannedItem) => {
          return (
            <Droppable key={plannedItem.id} droppableId={plannedItem.id}>
              {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
                return (
                  <WeekColumnDroppableStyled
                    style={numPlanningNextWeeks == 3 ? { width: '25%' } : { width: '33.33%' }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    {...snapshot}
                  >
                    <VisuallyHidden>{provided.placeholder}</VisuallyHidden>
                    {plannedItem && plannedItem.milestones && (
                      <CommodityCards
                        commodityMilestones={plannedItem.milestones}
                        droppableId={plannedItem.id}
                        setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
                        selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
                        draggingMilestoneId={draggingMilestoneId}
                        milestoneIdentifiers={milestoneIdentifiers}
                      />
                    )}
                  </WeekColumnDroppableStyled>
                );
              }}
            </Droppable>
          );
        })}
    </Fragment>
  );
};

const PlanningViewDnd: React.FC<PlanProps> = ({ expiredMilestoneStats }) => {
  const { numPlanningNextWeeks } = useConfig();
  const { selectedWpList, selectedForeman } = usePlanning();
  // NOTE: these state entities are only used for multi drag
  const [selectedMilestoneDraggableIds, setSelectedMilestoneDraggableIds] = useState<string[]>([]);
  const [draggingMilestoneId, setDraggingMilestoneId] = useState<string>('');
  const backlogMilestoneData = React.useRef<CommodityMilestone[]>([]);
  const planningWeekStats = React.useRef<PlanningWeekStats[]>([]);
  const plannedMilestoneData = React.useRef<PlanningWeek[]>([]);
  const milestoneIdentifiers = React.useRef<Map<string, MilestoneIdentifier>>(new Map());
  // const [claimedApprovedMilestones, setClaimedApprovedMilestone] = useState<CommodityMilestone[]>([]);
  const [planningError, setPlanningError] = useState<PlanningError[]>([]);
  const [savePlan, { error: savePlanError }] =
    useMutation<{ savePlanning: PlanningPayload }, { input: SavePlanningInput }>(SAVE_PLAN);
  const [unplan] = useMutation<{ unplan: PlanningPayload }, { input: UnplanInput }>(UNPLAN);
  const onDragStartCallback = useCallback(
    (start: DragStart) => {
      const id: string = start.draggableId;
      const selectedMilestoneDraggableId = find(selectedMilestoneDraggableIds, (milestoneId) => milestoneId === id);
      // if dragging an item that is not selected - unselect all items
      if (!selectedMilestoneDraggableId) {
        setSelectedMilestoneDraggableIds([]);
      }
      setDraggingMilestoneId(id);
    },
    [selectedMilestoneDraggableIds]
  );

  const onDragEndMulti = async ({ destination }, selectedMilestoneDraggableIds) => {
    const commodityMilestoneIds: string[] = [];
    const commodityMilestonesForOptimisticUpdate: CommodityMilestone[] = [];
    const milestoneSource: Map<string | undefined, string | undefined> = new Map();
    // NOTE: we bail out early if the source was not dropped into a drop target
    forEach(selectedMilestoneDraggableIds, (draggableId) => {
      if (!destination) return;
      // NOTE: a drag id is a composition of up to five things:
      // {droppable.id}___COMMODITY___{commodity.id}___MILESTONE___{milestone.id}
      // if just a commodity is dragged then it will just be the first three entities
      const identifiers = splitIdentifiers(draggableId);
      // NOTE: this will let us know where the item originated from based on droppable.id
      const sourceStateKey = nth(identifiers, 0);
      // NOTE: this identifier lets us know what column we will be dropping into
      // ideally this is something unique to the given week (such as the date)
      const destinationStateKey = destination.droppableId;
      // NOTE: we bail out early if the drop target is the same as the source
      if (sourceStateKey === destinationStateKey) return;
      // NOTE: milestone.id may NOT exist since a user can choose between dragging a milestone or
      // the entire commodity all at once
      const milestoneId = nth(identifiers, 4);
      milestoneSource.set(milestoneId, sourceStateKey);
      // NOTE: If the commodityId is exists and the milestoneId doesn't exists that means user dragged a commodity.
      // get all the milestones id from the commodity;
      if (sourceStateKey !== DND_IDENTIFIER.Unplanned) {
        const sourcePlaningWeek = find(plannedMilestoneData.current, { id: sourceStateKey });
        const foundCommodityMilestone = find(
          sourcePlaningWeek?.milestones,
          (milestone) => milestone.id === milestoneId
        );
        if (foundCommodityMilestone) {
          commodityMilestoneIds.push(foundCommodityMilestone?.id);
          commodityMilestonesForOptimisticUpdate.push(foundCommodityMilestone);
        }
      } else {
        const foundCommodityMilestone = find(backlogMilestoneData.current, (milestone) => milestone.id === milestoneId);
        if (foundCommodityMilestone) {
          commodityMilestoneIds.push(foundCommodityMilestone?.id);
          commodityMilestonesForOptimisticUpdate.push(foundCommodityMilestone);
        }
      }
    });
    if (commodityMilestoneIds.length > 0) {
      const destinationStateKey = destination.droppableId;
      const destPlanningWeekIdentifiers = splitIdentifiers(destinationStateKey, '|');
      //@ts-ignore destPlaningWeek be PlanningWeek|undefined
      const destPlaningWeek: PlanningWeek | undefined = find(plannedMilestoneData.current, {
        id: destinationStateKey,
      });
      if (!destPlaningWeek) return;
      const savePlanningInput: SavePlanningInput = {
        planningWeekId: String(nth(destPlanningWeekIdentifiers, 2)),
        year: parseInt(String(nth(destPlanningWeekIdentifiers, 0))),
        week: parseInt(String(nth(destPlanningWeekIdentifiers, 1))),
        commodityMilestoneIds: commodityMilestoneIds,
      };
      //@ts-ignore destPlaningWeek will be PlanningWeek object
      const optimisticResponse = getSavePlanningOptimisticResponse(
        destPlaningWeek,
        commodityMilestonesForOptimisticUpdate,
        planningWeekStats.current,
        milestoneSource,
        expiredMilestoneStats?.current
      );
      await savePlan({
        variables: {
          input: savePlanningInput,
        },
        optimisticResponse: optimisticResponse,
        update: (cache, { data }) => {
          if (data && data.savePlanning.commodityMilestones && data.savePlanning.commodityMilestones.length > 0) {
            forEach(data.savePlanning.commodityMilestones, (commodityMilestone) => {
              const sourceStateKey = milestoneSource.get(commodityMilestone.id);
              if (!sourceStateKey) return;
              updateCache(
                cache,
                sourceStateKey,
                destinationStateKey,
                [commodityMilestone],
                selectedForeman ? selectedForeman?.id : '',
                selectedWpList ? selectedWpList : [],
                numPlanningNextWeeks
              );
            });
          }

          if (data && data.savePlanning.error && data.savePlanning.error.length > 0) {
            setPlanningError(data.savePlanning.error);
            deleteApproveClaimedMilestone(
              data.savePlanning.error,
              milestoneSource,
              cache,
              destinationStateKey,
              selectedForeman ? selectedForeman?.id : '',
              selectedWpList ? selectedWpList : [],
              numPlanningNextWeeks
            );
          }
        },
      });
    }
  };

  const onDragEndSingle = async ({ destination, draggableId, source }) => {
    // NOTE: we bail out early if the source was not dropped into a drop target
    if (!destination) return;
    //console.log(planningWeekStats.current);

    const [sourceStateKey] = splitIdentifiers(source.droppableId); //sourceStateKey: "2020|21|3071629f-356e-428a-8ce2-f55938bb8055"
    // NOTE: this identifier lets us know what column we will be dropping into
    // ideally this is something unique to the given week (such as the date)
    const destinationStateKey = destination.droppableId; //"destinationStateKey: 2020|21|3071629f-356e-428a-8ce2-f55938bb8055"
    // NOTE: we bail out early if the drop target is the same as the source
    if (sourceStateKey === destinationStateKey) return;
    // NOTE: a drag id is a composition of up to five things:
    // {droppable.id}___COMMODITY___{commodity.id}___MILESTONE___{milestone.id}
    // if just a commodity is dragged then it will just be the first three entities
    const identifiers = splitIdentifiers(draggableId);
    // NOTE: we know commodity.id will always exist on a draggable entity
    const commodityId = nth(identifiers, 2);
    // NOTE: milestone.id may NOT exist since a user can choose between dragging a milestone or
    // the entire commodity all at once
    const milestoneId = nth(identifiers, 4);
    let commodityMilestoneIds = milestoneId ? [milestoneId] : [];
    const commodityMilestonesForOptimisticUpdate: CommodityMilestone[] = [];
    // NOTE: If the commodityId is exists and the milestoneId doesn't exists that means user dragged a commodity.
    // get all the milestones id from the commodity;
    if (sourceStateKey !== DND_IDENTIFIER.Unplanned) {
      const sourcePlaningWeek = find(plannedMilestoneData.current, { id: sourceStateKey });
      const foundCommodityMilestones = filter(sourcePlaningWeek?.milestones, (milestone) => {
        if (!milestoneId && commodityId) {
          return (
            milestone.commodity.materialCommodity?.id === commodityId || milestone.commodity.weld?.id === commodityId
          );
        } else {
          return milestone.id === milestoneId;
        }
      });
      const foundCommodityMilestoneIds = map(foundCommodityMilestones, ({ id }) => id);
      commodityMilestoneIds = foundCommodityMilestoneIds;
      commodityMilestonesForOptimisticUpdate.push(...foundCommodityMilestones);
    } else {
      const foundCommodityMilestones = filter(backlogMilestoneData.current, (milestone) => {
        if (!milestoneId && commodityId) {
          return (
            milestone.commodity.materialCommodity?.id === commodityId || milestone.commodity.weld?.id === commodityId
          );
        } else {
          return milestone.id === milestoneId;
        }
      });
      const foundCommodityMilestoneIds = map(foundCommodityMilestones, ({ id }) => id);
      commodityMilestoneIds = foundCommodityMilestoneIds;
      commodityMilestonesForOptimisticUpdate.push(...foundCommodityMilestones);
    }

    const destPlanningWeekIdentifiers = splitIdentifiers(destinationStateKey, '|');
    //@ts-ignore destPlaningWeek be PlanningWeek|undefined
    const destPlaningWeek: PlanningWeek | undefined = find(plannedMilestoneData.current, { id: destinationStateKey });
    if (!destPlaningWeek) return;
    const savePlanningInput: SavePlanningInput = {
      planningWeekId: String(nth(destPlanningWeekIdentifiers, 2)),
      year: parseInt(String(nth(destPlanningWeekIdentifiers, 0))),
      week: parseInt(String(nth(destPlanningWeekIdentifiers, 1))),
      commodityMilestoneIds: commodityMilestoneIds,
    };
    const milestoneSource: Map<string | undefined, string | undefined> = new Map();
    forEach(commodityMilestoneIds, (commodityMilestoneId) => milestoneSource.set(commodityMilestoneId, sourceStateKey));
    //@ts-ignore destPlaningWeek will be PlanningWeek object
    const optimisticResponse = getSavePlanningOptimisticResponse(
      destPlaningWeek,
      commodityMilestonesForOptimisticUpdate,
      planningWeekStats.current,
      milestoneSource,
      expiredMilestoneStats?.current
    );
    await savePlan({
      variables: {
        input: savePlanningInput,
      },
      optimisticResponse: optimisticResponse,
      update: (cache, { data }) => {
        if (data && data.savePlanning.commodityMilestones && data.savePlanning.commodityMilestones.length > 0) {
          updateCache(
            cache,
            sourceStateKey,
            destinationStateKey,
            data.savePlanning.commodityMilestones,
            selectedForeman ? selectedForeman?.id : '',
            selectedWpList ? selectedWpList : [],
            numPlanningNextWeeks
          );
        }
        if (data && data.savePlanning.error && data.savePlanning.error.length > 0) {
          setPlanningError(data.savePlanning.error);
          deleteApproveClaimedMilestone(
            data.savePlanning.error,
            milestoneSource,
            cache,
            destinationStateKey,
            selectedForeman ? selectedForeman?.id : '',
            selectedWpList ? selectedWpList : [],
            numPlanningNextWeeks
          );
        }
      },
    });
  };

  const onDragEndCallback = useCallback(
    (dragProps) => {
      if (selectedMilestoneDraggableIds.length > 0) {
        onDragEndMulti(dragProps, selectedMilestoneDraggableIds);
      } else {
        //One commodity
        onDragEndSingle(dragProps);
      }
      // NOTE: we can comment this next line out to
      // "persist" the selected state after a drag n drop action
      setSelectedMilestoneDraggableIds([]);
      setDraggingMilestoneId('');
    },
    [selectedMilestoneDraggableIds]
  );

  const handleUnplanMilestones = async () => {
    const commodityMilestoneIds: string[] = [];
    const commodityMilestonesForOptimisticUpdate: CommodityMilestone[] = [];
    const milestoneSource: Map<string | undefined, string | undefined> = new Map();
    //destination is backlog
    const destinationStateKey = DND_IDENTIFIER.Unplanned;
    forEach(selectedMilestoneDraggableIds, (draggableId) => {
      // NOTE: a drag id is a composition of up to five things:
      // {droppable.id}___COMMODITY___{commodity.id}___MILESTONE___{milestone.id}
      const identifiers = splitIdentifiers(draggableId);
      // NOTE: this will let us know where the item originated from based on droppable.id
      const sourceStateKey = nth(identifiers, 0);
      const isMilestoneExpired = nth(identifiers, 5) === DND_IDENTIFIER.Expired;
      //return if user tries to unplan already unplanned milestones unless they are expired
      if (sourceStateKey === destinationStateKey && !isMilestoneExpired) return;

      const milestoneId = nth(identifiers, 4);
      milestoneSource.set(milestoneId, sourceStateKey);
      if (sourceStateKey === DND_IDENTIFIER.Unplanned && isMilestoneExpired) {
        const foundCommodityMilestone = find(backlogMilestoneData.current, (milestone) => milestone.id === milestoneId);
        if (foundCommodityMilestone) {
          commodityMilestoneIds.push(foundCommodityMilestone?.id);
          commodityMilestonesForOptimisticUpdate.push(foundCommodityMilestone);
        }
      } else if (destinationStateKey === DND_IDENTIFIER.Unplanned) {
        //unplan planned milestones back to backlog
        forEach(plannedMilestoneData.current, (planninkWeek) => {
          if (!planninkWeek || !planninkWeek.milestones || !planninkWeek.milestones.length) return;
          const foundCommodityMilestone = find(planninkWeek.milestones, (milestone) => milestone.id === milestoneId);
          if (foundCommodityMilestone) {
            commodityMilestoneIds.push(foundCommodityMilestone?.id);
            commodityMilestonesForOptimisticUpdate.push(foundCommodityMilestone);
          }
        });
      }
    });
    if (commodityMilestoneIds && commodityMilestoneIds.length) {
      const optimisticResponse = getUnplanOptimisticResponse(
        commodityMilestonesForOptimisticUpdate,
        planningWeekStats.current,
        milestoneSource,
        expiredMilestoneStats?.current
      );

      const unplanInput: UnplanInput = {
        commodityMilestoneIds: commodityMilestoneIds,
      };
      await unplan({
        variables: {
          input: unplanInput,
        },
        optimisticResponse: optimisticResponse,
        update: (cache, { data }) => {
          if (data && data.unplan && data.unplan.commodityMilestones && data.unplan.commodityMilestones.length) {
            updateUnplanCache(
              cache,
              destinationStateKey,
              data.unplan.commodityMilestones,
              selectedWpList ? selectedWpList : [],
              selectedForeman ? selectedForeman?.id : '',
              numPlanningNextWeeks
            );
          }
        },
      });
    }
  };

  const unplanMilestonesCallback = useCallback(() => {
    if (selectedMilestoneDraggableIds && selectedMilestoneDraggableIds.length) {
      handleUnplanMilestones();
      setSelectedMilestoneDraggableIds([]);
    }
  }, [selectedMilestoneDraggableIds]);

  return (
    <PlanningContentContainer>
      {savePlanError && <SavePlanError savePlanError={savePlanError} />}
      {planningError && planningError.length > 0 && (
        <PlanMilestoneError planningError={planningError} setPlanningError={setPlanningError} />
      )}
      <DragDropContext onDragEnd={onDragEndCallback} onDragStart={onDragStartCallback}>
        <Backlog
          setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
          selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
          draggingMilestoneId={draggingMilestoneId}
          unplanMilestones={unplanMilestonesCallback}
          milestoneIdentifiers={milestoneIdentifiers}
          backlogMilestoneData={backlogMilestoneData}
        />
        <ColumnContainerStyled style={numPlanningNextWeeks == 3 ? { width: '79%' } : { width: '74%' }}>
          <PlanningWeekCard planWeekStats={planningWeekStats} />
          <WeekPlanDetailScrollable>
            <WeekPlanDetailContainer>
              <PlannedMilestones
                setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
                selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
                draggingMilestoneId={draggingMilestoneId}
                milestoneIdentifiers={milestoneIdentifiers}
                plannedMilestonesData={plannedMilestoneData}
              />
            </WeekPlanDetailContainer>
          </WeekPlanDetailScrollable>
        </ColumnContainerStyled>
      </DragDropContext>
    </PlanningContentContainer>
  );
};

const foremanToast = (numPlanningNextWeeks: number) => {
  return (
    <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
      <Box mb={{ sm: '450' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>You must select a Foreman before you can</Trans>
        </Text>
      </Box>
      <Text family="graphik" weight="600" size="500">
        {t`make changes to the ${numPlanningNextWeeks == 3 ? 'Four' : 'Three'}-Week Look Ahead.`}
      </Text>
    </Box>
  );
};

export interface PlanProps {
  expiredMilestoneStats?: MutableRefObject<CrewMemberExpiredMilestoneStats>;
}

export const Plan: React.FC<PlanProps> = ({ expiredMilestoneStats }) => {
  const { numPlanningNextWeeks } = useConfig();
  const { selectedWpList, selectedForeman } = usePlanning();
  const debouncedSearchQuery = useDebounce(selectedWpList, 500);
  const dispatchToast = useToastsDispatch();
  const [showForemanToast, setShowForemanToast] = useState<boolean>(true);
  const foremanSelectToast: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      id: TOAST_ID_SELECT_FOREMAN,
      toastType: ToastType.INFO,
      content: () => foremanToast(numPlanningNextWeeks),
      alertAction: {
        label: 'GOT IT',
        onClickHandler: () => {
          noop;
        },
      },
    },
  };
  const [loadWorkpackages, { loading, data, error, refetch, networkStatus }] = useLazyQuery<
    {
      workPackagesByIds: WorkPackage[];
    },
    {
      ids: string[];
    }
  >(GET_WORKPACKAGE_ADDITIONAL_INFO_BY_IDS);

  useEffect(() => {
    if (!selectedWpList || selectedWpList.length < 1) return;
    loadWorkpackages({
      variables: {
        ids: selectedWpList,
      },
    });
  }, [debouncedSearchQuery, selectedWpList]);

  useEffect(() => {
    if (showForemanToast) {
      dispatchToast(foremanSelectToast);
      setShowForemanToast(!showForemanToast);
    }
    if (selectedForeman && !(selectedForeman.id == AllForemanId || selectedForeman.id == UnassignedForemanId)) {
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: foremanSelectToast.payload.id } });
    }
  }, [selectedForeman, dispatchToast, showForemanToast, foremanSelectToast]);

  const workPackageDetails: WorkPackage[] = useMemo(() => {
    if (!selectedWpList || selectedWpList.length < 1) return [];
    return get(data, 'workPackagesByIds', []);
  }, [data, selectedWpList]);

  return (
    <>
      <PlanningActionContainer data-testid="planning-actions">
        <PlanningActions
          workPackages={workPackageDetails}
          loading={loading || networkStatus === NetworkStatus.refetch}
        />
      </PlanningActionContainer>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry
            refetch={() =>
              refetch({
                ids: selectedWpList && selectedWpList.length > 0 ? selectedWpList : [],
              })
            }
          >
            <Trans>There was a problem loading header content. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <WorkPackageDetailHeader
          isPlanningModule={true}
          showSecondaryOwners={false}
          workPackages={workPackageDetails}
        />
      )}
      <PlanningViewDnd expiredMilestoneStats={expiredMilestoneStats} />
    </>
  );
};
